import mixpanel from 'mixpanel-browser';
// i18n
import './locales/i18n';

// highlight
import './utils/highlight';

// scroll bar
import 'simplebar/src/simplebar.css';

// lightbox
import 'react-image-lightbox/style.css';


// editor
import 'react-quill/dist/quill.snow.css';

// slick-carousel
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

// lazy image
import 'react-lazy-load-image-component/src/effects/blur.css';
import 'react-lazy-load-image-component/src/effects/opacity.css';
import 'react-lazy-load-image-component/src/effects/black-and-white.css';

import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
// @mui
// contexts
import { SettingsProvider } from './contexts/SettingsContext';
import { CollapseDrawerProvider } from './contexts/CollapseDrawerContext';
import { ThemeContextProvider } from "./contexts/ThemeContext";

// Check our docs
// https://docs-minimals.vercel.app/authentication/ts-version

import { AuthProvider } from './contexts/AwsCognitoContext';
import { MerchantProvider } from './contexts/MerchantContext';

//
import App from './App';
import * as Sentry from "@sentry/react";
import * as serviceWorkerRegistration from './serviceWorkerRegistration';
import reportWebVitals from './reportWebVitals';
import { CarbonUserProvider } from './contexts/CarbonUserContext';
import { ENVIRONMENT_NAME, MUI_LICENSE_KEY, SEMVER, SENTRY_DSN } from "./config";
import { SubscriptionsContextProvider } from "./contexts/SubscriptionsContext";
import NoInternetConnection from './pages/NoInternetConnection';
import { QueryClient, QueryClientProvider } from 'react-query'

import { LicenseInfo } from '@mui/x-license-pro';

LicenseInfo.setLicenseKey(MUI_LICENSE_KEY);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false // retries are already configured in axios
    },
    mutations: {
      retry: false // retries are already confifured in axios
    }
  }
})

// ----------------------------------------------------------------------

const mixpanelToken = process.env.REACT_APP_MIXPANEL_TOKEN;

if (mixpanelToken) {
  mixpanel.init(mixpanelToken);
}
else{
    console.error("REACT_APP_MIXPANEL_TOKEN is not defined");
}
// ----------------------------------------------------------------------

Sentry.init({
  release: SEMVER,
  environment: ENVIRONMENT_NAME,
  dsn: SENTRY_DSN,
  ignoreErrors: [
    "ResizeObserver loop limit exceeded",
    "offsetWidth",
    "Failed to fetch",
    "Network request failed",
    "NetworkError",
    "DOMException: Failed to read the 'localStorage' property from 'Window'",
    /Loading chunk \d+ failed. *\n*\(timeout:/g,
    /Network Error/g,
    /timeout/g,
    /The request timed out/g,
    /Request aborted/g,
    /Network error/g,
  ],
  beforeSend: (event) => {
    const notFoundFlightSettings = event?.breadcrumbs?.find((el: any) => 
      el?.data?.status_code === 404 && el?.data?.method === 'GET' && el?.data?.url?.includes('/flight-calculator/active'));
    
    if (notFoundFlightSettings) {
      // do not send known issue
      return null;
    }

    return event;
  },
});

// ----------------------------------------------------------------------

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

root.render(
  <BrowserRouter>
    <NoInternetConnection>
      <AuthProvider>
        <CarbonUserProvider>
          <MerchantProvider>
            <SubscriptionsContextProvider>
              <ThemeContextProvider>
                <HelmetProvider>
                    <SettingsProvider>
                      <CollapseDrawerProvider>
                        <QueryClientProvider client={queryClient}>
                          <App/>
                        </QueryClientProvider>
                      </CollapseDrawerProvider>
                    </SettingsProvider>
                </HelmetProvider>
              </ThemeContextProvider>
            </SubscriptionsContextProvider>
          </MerchantProvider>
        </CarbonUserProvider>
      </AuthProvider>
    </NoInternetConnection>
  </BrowserRouter>
);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://cra.link/PWA
serviceWorkerRegistration.unregister();

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
